import { UseQueryResult } from '@tanstack/react-query';
import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { IContentCalendarSelectedSuggestion, IImpersonationContextComponentProps } from '../../../../../../models';
import { useEnvironment } from '../../../../../../models/hooks/appStateHooks';
import { createContentStateWithHtmlStringValue } from '../../../../../../models/UiUtils';
import { useAccountProfileQuery } from '../../../../../../queries';
import { AiButton } from '../../../../../components-v2/AiButton';
import { Button } from '../../../../../components-v2/Button';
import { CampaignDayPicker } from '../../../../../components/CampaignDayPicker';
import { DeleteConfirmation } from '../../../../../components/DeleteConfirmation';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../../../../../components/HoverCard';
import { LoadingSpinner } from '../../../../../components/LoadingSpinner';
import { Popover, PopoverArrow, PopoverContent, PopoverTrigger } from '../../../../../components/Popover';
import {
	IRichContentDocumentEditorConfig,
	RichContentDocumentEditor,
} from '../../../../../components/richContent/RichContentDocumentEditor';
import { Separator } from '../../../../../components/Separator';
import { CalendarIcon } from '../../../../../components/svgs/icons/CalendarIcon';
import { CheckmarkIconBold } from '../../../../../components/svgs/icons/CheckmarkIconBold';
import { TrashIcon } from '../../../../../components/svgs/icons/TrashIcon';
import { TextInput } from '../../../../../components/TextInput';
import { Toggle } from '../../../../../components/Toggle';
import { aceGray, destructive, success } from '../../../../../styles/colors';
import { bs } from '../../../../../styles/styles';
import { GeneratingContentLoader } from '../../emails/MultiEmailTemplateItem/generatingContentLoader';
import { styleSheet } from './styles';

interface IProps extends IImpersonationContextComponentProps {
	canRemove?: boolean;
	error?: Api.IOperationResultNoValue;
	onRemove?: () => void;
	onUpdateDate: (newDate: Date) => void;
	query: UseQueryResult<Api.ITemplate, unknown>;
	scheduled?: boolean;
	scheduleDate: Date;
	styles?: StyleDeclarationValue[];
	suggestion: IContentCalendarSelectedSuggestion;
	postedOn?: Date | string;
	isGeneratingContent: boolean;
	generationFailed: boolean;
	onGenerateContent: () => void;
	onShowOriginalContent: () => void;
	showOriginalContent: boolean;
	generatedContent: Api.IRawRichTextContentState;
}

const defaultEditorConfig: IRichContentDocumentEditorConfig = {
	plugins: null,
	toolbar: null,
};

export function MultiBlogTemplateItem({
	query,
	onRemove,
	scheduled,
	scheduleDate,
	error,
	onUpdateDate,
	postedOn,
	isGeneratingContent,
	generationFailed,
	onGenerateContent,
	onShowOriginalContent,
	showOriginalContent,
	generatedContent,
	impersonationContext,
}: IProps) {
	const isAdminView = useEnvironment()?.appType === 'admin';
	const accountProfileQuery = useAccountProfileQuery({ impersonationContext });
	const { data: template } = query;
	const [isShowingCalendar, setIsShowingCalendar] = React.useState(false);
	const [isRemoving, setIsRemoving] = React.useState(false);
	const onDateSave = (newValue: Date) => {
		onUpdateDate(newValue);
		setIsShowingCalendar(false);
	};
	const onDeleteConfirmationFinished = (shouldDelete: boolean) => {
		if (shouldDelete) {
			onRemove();
		}
		setIsRemoving(false);
	};

	const content = React.useMemo<Api.IRichContentEditorState>(() => {
		if (generatedContent && !showOriginalContent) {
			const templateString = generatedContent.document.replace(
				new RegExp(`</?${Api.RawRichTextContentDocumentRootElementName}>`, 'gim'),
				''
			);
			return createContentStateWithHtmlStringValue(templateString);
		}

		if (template) {
			const contentString = template.content.document.replace(
				new RegExp(`</?${Api.RawRichTextContentDocumentRootElementName}>`, 'gim'),
				''
			);
			return createContentStateWithHtmlStringValue(contentString);
		}
		return null;
	}, [template, generatedContent, showOriginalContent]);

	const toggleId = React.useId();

	return (
		<div className={css(styleSheet.MultiCampaignTemplateItem)}>
			<div className={css(styleSheet.contentContainer)}>
				<header className={css(styleSheet.headerRow)}>
					<div>
						<div className={css(styleSheet.templateTitle)}>{template?.name || 'Loading...'}</div>
						{error ? <p className={css(styleSheet.errorMessage)}>Failed to post: {error.systemMessage}</p> : null}

						{postedOn ? (
							<p className={css(styleSheet.errorMessage)}>
								Posted from this account on {moment(postedOn).format('MM/DD/YYYY')}
							</p>
						) : null}
					</div>
					<div className={css(styleSheet.recommendedDateContainer)}>
						<Popover open={isShowingCalendar} onOpenChange={setIsShowingCalendar}>
							<PopoverTrigger asChild>
								<Button variant='secondary' size='sm' disabled={scheduled}>
									<CalendarIcon /> {!scheduleDate ? 'Add send date' : moment(scheduleDate).format('ddd, MMM Do h:mm a')}
									{scheduled ? <CheckmarkIconBold fillColor={success} /> : null}
								</Button>
							</PopoverTrigger>
							<PopoverContent styles={[bs.p0, bs.flex, bs.flexCol, bs.gap2]} side='left'>
								<PopoverArrow />
								<BlogDateTimePicker
									initialValue={moment(scheduleDate ?? undefined).toDate()}
									onSave={onDateSave}
									onCancel={() => {
										setIsShowingCalendar(false);
									}}
								/>
							</PopoverContent>
						</Popover>
						{isAdminView && onGenerateContent && accountProfileQuery.data?.businessDescription ? (
							<AiButton size='sm' onClick={() => onGenerateContent()} isLoading={isGeneratingContent} />
						) : null}
						<Button
							variant='destructive'
							size='sm'
							disabled={scheduled || !onRemove}
							onClick={() => setIsRemoving(true)}
						>
							<TrashIcon fillColor={destructive} />
						</Button>
					</div>
				</header>

				{isAdminView && (
					<div className={css(styleSheet.generateContentInfoContainer)}>
						{generationFailed ? <p className={css(styleSheet.errorMessage)}>Failed to generate content</p> : null}

						{onShowOriginalContent && (
							<div className={css(styleSheet.showOriginalContentToggle)}>
								<Toggle
									text='Show original content'
									id={toggleId}
									isOn={showOriginalContent}
									onToggleCheckChanged={onShowOriginalContent}
									uncheckedColor={aceGray}
								/>
							</div>
						)}
					</div>
				)}

				<div>
					<div className={css(styleSheet.stack)}>
						<HoverCard>
							<HoverCardTrigger asChild>
								<img src={template?.attachments?.[0]?.url} alt='Preview' className={css(styleSheet.image)} />
							</HoverCardTrigger>
							<HoverCardContent side='right' styles={[bs.p2]}>
								<img src={template?.attachments?.[0]?.url} alt='Preview' className={css(styleSheet.largeImage)} />
							</HoverCardContent>
						</HoverCard>
						<div
							className={css(
								styleSheet.editorContainer,
								scheduled ? styleSheet.editorContainerSuccess : null,
								error ? styleSheet.editorContainerError : null
							)}
						>
							<GeneratingContentLoader
								isGeneratingContent={isGeneratingContent}
								className={css(styleSheet.generatingContentLoader)}
							/>

							{!content ? (
								<LoadingSpinner key='loading' type='small' />
							) : (
								<RichContentDocumentEditor
									className={css(styleSheet.editor)}
									config={defaultEditorConfig}
									contentState={content}
									readOnly={true}
								/>
							)}
						</div>
					</div>
				</div>
			</div>
			{isRemoving ? (
				<DeleteConfirmation
					bodyText={`Are you sure you'd like to remove ${template?.name} from selected suggestions?`}
					destructiveButtonText='Remove'
					isOpen={isRemoving}
					onFinish={onDeleteConfirmationFinished}
				/>
			) : null}
		</div>
	);
}

function BlogDateTimePicker({
	initialValue,
	onSave,
	onCancel,
}: {
	initialValue: Date;
	onSave: (value: Date) => void;
	onCancel: () => void;
}) {
	const [value, setValue] = React.useState<Date>(initialValue);
	const [time, setTime] = React.useState<string>(moment(initialValue).format('HH:mm'));

	const handleSave = () => {
		const [hours, minutes] = time.split(':').map(n => parseInt(n));
		const newValue = new Date(value);
		newValue.setHours(hours, minutes, 0, 0);
		onSave(newValue);
	};
	const onDaySelected = (day: Date) => {
		day.setHours(value.getHours());
		setValue(day);
	};

	return (
		<div className={css(styleSheet.blogPickerRoot)}>
			<CampaignDayPicker
				selectedDay={value}
				numWeeksShown={6}
				onDayClick={onDaySelected}
				modifiers={null}
				allowPastDates={true}
				styles={[bs.m0, bs.wFull]}
			/>
			<div className={css(styleSheet.timeAndFooter)}>
				<Separator styles={[bs.mt4]} />
				<TextInput
					className={css(styleSheet.blogPickerTime)}
					type='time'
					step='60'
					value={time}
					onChange={ev => setTime(ev.target.value)}
				/>
				<Separator styles={[bs.mt4]} />
				<div className={css(styleSheet.footer)}>
					<Button onClick={handleSave}>Schedule</Button>
					<Button variant='secondary' onClick={onCancel}>
						Cancel
					</Button>
				</div>
			</div>
		</div>
	);
}
